import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  FlaskConical, Check, X as XIcon, Trash2, Search, Users, FileText,
  ChevronDown, ChevronUp, Pencil, Eye, ArrowLeft, Save, Globe, Target,
  Tag, Zap, AlertCircle, FileSearch
} from 'lucide-react';
import { toast } from 'sonner';
import { AdminBriteLabsResearchTab } from './AdminBriteLabsResearchTab';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';

const BACKEND_URL = '';

const STATUS_STYLES = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  solved: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
};

const CATEGORIES = [
  "AI Adoption", "Air", "Carbon Accounting", "Certification",
  "Change Management", "Commercialisation", "Electrification", "Freight",
  "Ocean", "Rail", "Road", "Warehousing/ Fulfilment", "Other"
];

const IMPACT_LEVELS = [
  { value: "individual", label: "Individual" },
  { value: "organisation", label: "Organisation" },
  { value: "industry", label: "Industry" },
  { value: "cross_industry", label: "Cross Industry" },
];

const STATUSES = ["pending", "active", "solved", "rejected"];

function DetailRow({ label, icon: Icon, value, testId, rich }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2" data-testid={testId}>
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
        {rich
          ? <div className="text-sm text-slate-800 mt-0.5"><RichText html={value} className="compact" /></div>
          : <p className="text-sm text-slate-800 mt-0.5 whitespace-pre-wrap">{value}</p>}
      </div>
    </div>
  );
}

export function AdminBriteLabsTab() {
  const [view, setView] = useState('list');
  const [labs, setLabs] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [selectedLab, setSelectedLab] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadLabs(); }, []);

  const loadLabs = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/britelabs`, { credentials: 'include' });
      if (res.ok) setLabs(await res.json());
    } catch { toast.error('Failed to load BriteLabs'); }
  };

  const handleLabAction = async (labId, action) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/britelabs/${labId}/${action}`, { method: 'PUT', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success(`BriteLab ${action === 'approve' ? 'approved' : 'rejected'}`);
      loadLabs();
      if (selectedLab?.lab_id === labId) {
        setSelectedLab(prev => ({ ...prev, status: action === 'approve' ? 'active' : 'rejected' }));
      }
    } catch { toast.error('Action failed'); }
  };

  const handleDelete = async (labId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/britelabs/${labId}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast.success(data.message);
      setDeleteConfirm(null);
      if (selectedLab?.lab_id === labId) setSelectedLab(null);
      loadLabs();
    } catch { toast.error('Delete failed'); }
  };

  const openDetail = (lab) => {
    setSelectedLab(lab);
    setEditing(false);
    setEditForm({
      title: lab.title || '',
      root_cause: lab.root_cause || '',
      lab_type: lab.lab_type || 'problem',
      problem_statement: lab.problem_statement || '',
      category: lab.category || '',
      desired_outcome: lab.desired_outcome || '',
      country: lab.country || '',
      impact_level: lab.impact_level || '',
      status: lab.status || 'pending',
    });
  };

  const handleSave = async () => {
    if (!editForm.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/britelabs/${selectedLab.lab_id}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed'); }
      const updated = await res.json();
      toast.success('BriteLab updated');
      setSelectedLab({ ...selectedLab, ...updated });
      setEditing(false);
      loadLabs();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const filtered = labs.filter(lab => {
    const matchSearch = !search || lab.title?.toLowerCase().includes(search.toLowerCase()) ||
      lab.creator_name?.toLowerCase().includes(search.toLowerCase()) ||
      lab.category?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || lab.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statuses = ['all', ...new Set(labs.map(l => l.status))];

  // Detail/edit view
  if (selectedLab) {
    const lab = selectedLab;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setSelectedLab(null); setEditing(false); }}
            data-testid="back-to-labs-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h3 className="text-lg font-semibold flex-1">{editing ? 'Edit BriteLab' : 'BriteLab Detail'}</h3>
          <div className="flex gap-2">
            {!editing && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                data-testid="edit-britelab-btn">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
            )}
            {lab.status === 'pending' && !editing && (
              <>
                <Button size="sm" className="bg-brand-sage hover:bg-brand-sage/90 text-white"
                  onClick={() => handleLabAction(lab.lab_id, 'approve')} data-testid="detail-approve-btn">
                  <Check className="h-3.5 w-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50"
                  onClick={() => handleLabAction(lab.lab_id, 'reject')} data-testid="detail-reject-btn">
                  <XIcon className="h-3.5 w-3.5 mr-1" /> Reject
                </Button>
              </>
            )}
          </div>
        </div>

        <Card className="p-6">
          {editing ? (
            <div className="space-y-4" data-testid="britelab-edit-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600 mb-1 block">Title</Label>
                  <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                    data-testid="edit-lab-title" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Category</Label>
                  <select value={editForm.category} onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full h-9 px-3 border rounded-md text-sm bg-white" data-testid="edit-lab-category">
                    <option value="">Select...</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Status</Label>
                  <select value={editForm.status} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full h-9 px-3 border rounded-md text-sm bg-white" data-testid="edit-lab-status">
                    {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Type</Label>
                  <select value={editForm.lab_type} onChange={e => setEditForm(f => ({ ...f, lab_type: e.target.value }))}
                    className="w-full h-9 px-3 border rounded-md text-sm bg-white" data-testid="edit-lab-type">
                    <option value="problem">Problem</option>
                    <option value="question">Question</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Impact Level</Label>
                  <select value={editForm.impact_level} onChange={e => setEditForm(f => ({ ...f, impact_level: e.target.value }))}
                    className="w-full h-9 px-3 border rounded-md text-sm bg-white" data-testid="edit-lab-impact">
                    <option value="">Select...</option>
                    {IMPACT_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600 mb-1 block">Root Cause</Label>
                  <RichTextEditor value={editForm.root_cause} onChange={v => setEditForm(f => ({ ...f, root_cause: v }))}
                    testId="edit-lab-root-cause" minHeight="5rem" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600 mb-1 block">Problem Statement</Label>
                  <RichTextEditor value={editForm.problem_statement} onChange={v => setEditForm(f => ({ ...f, problem_statement: v }))}
                    testId="edit-lab-problem" minHeight="7rem" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs text-slate-600 mb-1 block">Desired Outcome</Label>
                  <RichTextEditor value={editForm.desired_outcome} onChange={v => setEditForm(f => ({ ...f, desired_outcome: v }))}
                    testId="edit-lab-outcome" minHeight="6rem" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 mb-1 block">Country</Label>
                  <Input value={editForm.country} onChange={e => setEditForm(f => ({ ...f, country: e.target.value }))}
                    data-testid="edit-lab-country" />
                </div>
              </div>
              <div className="flex gap-2 pt-2 border-t">
                <Button onClick={handleSave} disabled={saving} data-testid="save-britelab-btn"
                  className="bg-brand-navy text-white">
                  <Save className="h-3.5 w-3.5 mr-1.5" /> {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} data-testid="cancel-edit-btn">Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1" data-testid="britelab-detail-view">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[lab.status] || 'bg-slate-100 text-slate-600'}`}
                  data-testid="detail-status">
                  {lab.status?.charAt(0).toUpperCase() + lab.status?.slice(1)}
                </span>
                {lab.lab_type && (
                  <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-600" data-testid="detail-type">
                    {lab.lab_type === 'question' ? 'Question' : 'Problem'}
                  </span>
                )}
                <span className="text-xs text-slate-400">Created {new Date(lab.created_at).toLocaleDateString()}</span>
              </div>

              <h2 className="text-xl font-bold text-slate-800 mb-3" data-testid="detail-title">{lab.title}</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-1">
                <DetailRow label="Category" icon={Tag} value={lab.category} testId="detail-category" />
                <DetailRow label="Impact Level" icon={Zap} value={
                  IMPACT_LEVELS.find(l => l.value === lab.impact_level)?.label || lab.impact_level
                } testId="detail-impact" />
                <DetailRow label="Country" icon={Globe} value={lab.country} testId="detail-country" />
                <DetailRow label="Creator" icon={Users} value={lab.creator_name} testId="detail-creator" />
              </div>

              <div className="border-t mt-3 pt-3 space-y-1">
                <DetailRow label="Root Cause" icon={AlertCircle} value={lab.root_cause} testId="detail-root-cause" rich />
                <DetailRow label="Problem Statement" icon={FileText} value={lab.problem_statement} testId="detail-problem" rich />
                <DetailRow label="Desired Outcome" icon={Target} value={lab.desired_outcome} testId="detail-outcome" rich />
              </div>

              <div className="border-t mt-3 pt-3">
                <div className="flex gap-6 text-sm text-slate-500">
                  <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {lab.stakeholder_count || lab.stakeholders?.length || 0} members</span>
                  <span className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> {lab.post_count || 0} posts</span>
                  <span>Milestones: {lab.milestones?.length || 0}</span>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    );
  }

  // List view
  // Top-level wrapper with sub-tabs: BriteLabs list  |  Research Management
  const subNav = (
    <div className="flex gap-1 border-b border-slate-200" data-testid="britelabs-subnav">
      <button onClick={() => { setView('list'); setSelectedLab(null); }} data-testid="britelabs-subnav-list"
        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
          view === 'list'
            ? 'border-brand-navy text-brand-navy'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        }`}>
        <FlaskConical className="h-3.5 w-3.5 inline mr-1.5" /> BriteLabs
      </button>
      <button onClick={() => { setView('research'); setSelectedLab(null); }} data-testid="britelabs-subnav-research"
        className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
          view === 'research'
            ? 'border-brand-navy text-brand-navy'
            : 'border-transparent text-slate-500 hover:text-slate-700'
        }`}>
        <FileSearch className="h-3.5 w-3.5 inline mr-1.5" /> Research Management
      </button>
    </div>
  );

  if (view === 'research') {
    return (
      <div className="space-y-4">
        {subNav}
        <AdminBriteLabsResearchTab />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {subNav}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">All BriteLabs</h3>
          <p className="text-sm text-slate-500">{labs.length} total labs</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input data-testid="britelabs-search" placeholder="Search labs..." value={search}
              onChange={e => setSearch(e.target.value)} className="pl-8 w-56 h-9" />
          </div>
          <select data-testid="britelabs-status-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="h-9 px-3 border rounded-md text-sm bg-white">
            {statuses.map(s => <option key={s} value={s}>{s === 'all' ? 'All Status' : s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-8 text-center">
          <FlaskConical className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No BriteLabs found</p>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map(lab => (
            <Card key={lab.lab_id} data-testid={`admin-lab-${lab.lab_id}`} className="p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openDetail(lab)}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_STYLES[lab.status] || 'bg-slate-100 text-slate-600'}`}>
                      {lab.status?.charAt(0).toUpperCase() + lab.status?.slice(1)}
                    </span>
                    <span className="text-xs text-slate-400">{lab.category}</span>
                    {lab.impact_level && (
                      <span className="text-xs text-slate-400">| {lab.impact_level}</span>
                    )}
                  </div>
                  <h4 className="font-bold text-slate-800 hover:text-brand-navy transition-colors">{lab.title}</h4>
                  <div className="text-sm text-slate-600 mt-1"><RichText html={lab.problem_statement} className="card card-2" /></div>
                  {lab.desired_outcome && (
                    <div className="text-xs text-slate-500 mt-1 flex items-start gap-1">
                      <span className="font-medium flex-shrink-0">Outcome:</span>
                      <RichText html={lab.desired_outcome} className="card card-2 flex-1" />
                    </div>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                    <span>By {lab.creator_name}</span>
                    <span className="flex items-center gap-1"><Users className="h-3 w-3" />{lab.stakeholder_count} members</span>
                    <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{lab.post_count} posts</span>
                    <span>{new Date(lab.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 items-start">
                  <Button data-testid={`view-lab-${lab.lab_id}`} onClick={() => openDetail(lab)}
                    size="sm" variant="outline" className="h-8">
                    <Eye className="h-3.5 w-3.5 mr-1" /> View
                  </Button>
                  {lab.status === 'pending' && (
                    <>
                      <Button data-testid={`approve-lab-${lab.lab_id}`} onClick={() => handleLabAction(lab.lab_id, 'approve')}
                        size="sm" className="bg-brand-sage hover:bg-brand-sage/90 text-white h-8">
                        <Check className="h-3.5 w-3.5 mr-1" /> Approve
                      </Button>
                      <Button data-testid={`reject-lab-${lab.lab_id}`} onClick={() => handleLabAction(lab.lab_id, 'reject')}
                        size="sm" variant="outline" className="text-red-600 hover:bg-red-50 h-8">
                        <XIcon className="h-3.5 w-3.5 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {deleteConfirm === lab.lab_id ? (
                    <div className="flex gap-1.5 items-center">
                      <span className="text-xs text-red-600 font-medium">Delete?</span>
                      <Button data-testid={`confirm-delete-lab-${lab.lab_id}`} onClick={() => handleDelete(lab.lab_id)}
                        size="sm" variant="destructive" className="h-7 px-2 text-xs">Yes</Button>
                      <Button data-testid={`cancel-delete-lab-${lab.lab_id}`} onClick={() => setDeleteConfirm(null)}
                        size="sm" variant="outline" className="h-7 px-2 text-xs">No</Button>
                    </div>
                  ) : (
                    <Button data-testid={`delete-lab-${lab.lab_id}`} onClick={() => setDeleteConfirm(lab.lab_id)}
                      size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
